// EchoCodeSearch.js - Search bar for echo codes with keyboard navigation

import React, { useState, useEffect, useRef } from 'react';
import { searchCodes, detectCategoryPrefix, CATEGORY_NAMES } from './echo-codes/echoCodesLoader';
import './EchoCodeSearch.css';

/**
 * Search result item component
 */
const SearchResultItem = ({ codeObj, isSelected, onClick }) => {
  const categoryName = CATEGORY_NAMES[codeObj.category] || codeObj.category;

  return (
    <div
      className={`echo-search-result-item ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
    >
      <span className="echo-search-result-code">{codeObj.code}</span>
      <span className="echo-search-result-name">{codeObj.nom}</span>
      <span className="echo-search-result-category">{categoryName}</span>
    </div>
  );
};

/**
 * Search bar component for echo codes with keyboard navigation
 */
const EchoCodeSearch = ({ onAddCode, existingCodes }) => {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedModifierIndex, setSelectedModifierIndex] = useState(0);
  const [pendingCode, setPendingCode] = useState(null);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Handle clicks outside to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setPendingCode(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search when text changes
  useEffect(() => {
    if (searchText.trim() === '') {
      setResults([]);
      setShowResults(false);
      setSelectedIndex(0);
      setActiveCategoryFilter(null);
      return;
    }

    // Detect category prefix
    const prefixMatch = detectCategoryPrefix(searchText);

    let searchResults;
    if (prefixMatch) {
      // Category filter detected
      setActiveCategoryFilter({
        prefix: prefixMatch.prefix,
        category: prefixMatch.category,
        displayName: prefixMatch.displayName
      });

      // Search with category filter
      const textToSearch = prefixMatch.remainingText || '';
      if (textToSearch.length > 0) {
        searchResults = searchCodes(textToSearch, prefixMatch.category);
      } else {
        // Show all codes from the category if no additional text
        searchResults = searchCodes('', prefixMatch.category);
        // When no search text, return all codes from category
        const allCodes = require('./echo-codes/echoCodesLoader').loadAllEchoCodes();
        searchResults = allCodes.filter(c => c.category === prefixMatch.category);
      }
    } else {
      // No category filter
      setActiveCategoryFilter(null);
      searchResults = searchCodes(searchText);
    }

    // Filter out codes that already exist
    const filtered = searchResults.filter(codeObj => {
      const category = codeObj.category;
      const existingCategory = existingCodes[category] || [];
      return !existingCategory.some(existing => existing.code === codeObj.code);
    });

    setResults(filtered);
    setShowResults(true);
    setSelectedIndex(0); // Reset selection when results change
  }, [searchText, existingCodes]);

  // Scroll selected item into view
  useEffect(() => {
    if (showResults && resultsRef.current && selectedIndex >= 0) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [selectedIndex, showResults]);

  const handleSelectCode = (codeObj) => {
    // Check if code has modifiers (supplement)
    if (codeObj.supplement && codeObj.supplement.length > 0) {
      // Show modifier selection
      setPendingCode(codeObj);
      setSelectedModifierIndex(0); // Start with "Aucun" option
      setShowResults(false);
    } else {
      // Add directly without modifier
      onAddCode(codeObj.code, null);
      setSearchText('');
      setShowResults(false);
      setPendingCode(null);
    }
  };

  const handleConfirmWithModifier = (modifierIndex = selectedModifierIndex) => {
    if (pendingCode) {
      // Index 0 = "Aucun" (null), index 1+ = supplement array
      const modifier = modifierIndex === 0 ? null : pendingCode.supplement[modifierIndex - 1];
      onAddCode(pendingCode.code, modifier);
      setSearchText('');
      setPendingCode(null);
      setSelectedModifierIndex(0);
      // Focus back on input
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleCancelModifier = () => {
    setPendingCode(null);
    setSelectedModifierIndex(0);
    setShowResults(true);
    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRemoveCategoryFilter = () => {
    // Remove category prefix from search text
    if (activeCategoryFilter) {
      const prefixMatch = detectCategoryPrefix(searchText);
      if (prefixMatch) {
        setSearchText(prefixMatch.remainingText);
      }
    }
    setActiveCategoryFilter(null);
    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    // If showing modifier picker, handle differently
    if (pendingCode) {
      const totalOptions = 1 + pendingCode.supplement.length; // "Aucun" + modifiers

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          setSelectedModifierIndex((prev) => (prev - 1 + totalOptions) % totalOptions);
          break;

        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          setSelectedModifierIndex((prev) => (prev + 1) % totalOptions);
          break;

        case 'Enter':
          e.preventDefault();
          handleConfirmWithModifier();
          break;

        case 'Escape':
          e.preventDefault();
          handleCancelModifier();
          break;

        default:
          break;
      }
      return;
    }

    // Handle Escape to clear search
    if (e.key === 'Escape') {
      e.preventDefault();
      setSearchText('');
      setResults([]);
      setShowResults(false);
      setActiveCategoryFilter(null);
      return;
    }

    if (!showResults || results.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        break;

      case 'ArrowRight':
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelectCode(results[selectedIndex]);
        }
        break;

      default:
        break;
    }
  };

  return (
    <div className="echo-code-search" ref={searchRef}>
      <div className="echo-search-input-wrapper">
        {activeCategoryFilter && (
          <div className="echo-search-filter-chip">
            <span className="echo-filter-chip-label">
              {activeCategoryFilter.prefix}
            </span>
            <button
              className="echo-filter-chip-remove"
              onClick={handleRemoveCategoryFilter}
              title="Supprimer le filtre"
            >
              ✕
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          className="echo-search-input"
          placeholder="Rechercher un code..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setShowResults(true);
            }
          }}
        />
        {searchText && (
          <button
            className="echo-search-clear"
            onClick={() => {
              setSearchText('');
              setResults([]);
              setShowResults(false);
              setActiveCategoryFilter(null);
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
          >
            ✕
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="echo-search-results">
          <div className="echo-search-results-header">
            {results.length} résultat{results.length > 1 ? 's' : ''}
            {activeCategoryFilter && ` dans ${activeCategoryFilter.displayName}`}
            {' • ↑↓ naviguer • →/Entrée ajouter'}
          </div>
          <div className="echo-search-results-list" ref={resultsRef}>
            {results.slice(0, 20).map((codeObj, index) => (
              <SearchResultItem
                key={`${codeObj.code}-${index}`}
                codeObj={codeObj}
                isSelected={index === selectedIndex}
                onClick={() => handleSelectCode(codeObj)}
              />
            ))}
            {results.length > 20 && (
              <div className="echo-search-results-more">
                + {results.length - 20} autres résultats...
              </div>
            )}
          </div>
        </div>
      )}

      {showResults && results.length === 0 && searchText.trim() !== '' && (
        <div className="echo-search-no-results">
          Aucun code trouvé pour "{searchText}"
        </div>
      )}

      {pendingCode && (
        <div className="echo-search-modifier-picker">
          <div className="echo-modifier-picker-header">
            <strong>{pendingCode.code}</strong> - {pendingCode.nom}
            <span className="echo-modifier-picker-hint">↑↓ naviguer • Entrée valider • Échap annuler</span>
          </div>
          <div className="echo-modifier-picker-body">
            <div className="echo-modifier-buttons">
              <button
                className={`echo-modifier-btn ${selectedModifierIndex === 0 ? 'active' : ''}`}
                onClick={() => handleConfirmWithModifier(0)}
              >
                Aucun
              </button>
              {pendingCode.supplement.map((mod, index) => (
                <button
                  key={mod}
                  className={`echo-modifier-btn ${selectedModifierIndex === index + 1 ? 'active' : ''}`}
                  onClick={() => handleConfirmWithModifier(index + 1)}
                >
                  {mod}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EchoCodeSearch;
